// src/resources/dashboards/ProductEvolution/sections/ModulesSection.tsx
//
// Matriz contratado × em uso. Módulo contratado e parado é risco de downgrade
// na renovação; módulo não contratado com preço declarado é conversa de upsell.
//
// O rótulo "potencial declarado" viaja junto do número por contrato do backend
// (`upsellPotentialLabel`) e a `note` explica por quê: os campos `*_value` do
// cadastro NUNCA entram em cobrança — client_bill.value vem inteiro do Gestão
// Click. Sem esse aviso, o número é lido como previsão de receita.
import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ExtensionIcon from '@mui/icons-material/Extension';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { COLORS, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtBrl, fmtNum } from '../../shared';
import type { SectionState } from '../../shared';
import { ChainModulesData } from '../types';
import { BackendNote, Card, OnOffChip, Stat, StatGrid } from './kit';

const HEIGHT = 400;

/** Frase obrigatória sempre que o potencial de upsell aparece na tela. */
const UPSELL_TOOLTIP = 'Potencial declarado — os preços de módulo não entram na cobrança real.';

const Th = ({ children, align = 'right' }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) => (
  <TableCell align={align} sx={{
    borderBottom: '1px solid rgba(120,105,223,0.12)', whiteSpace: 'nowrap', py: 0.75,
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase', color: COLORS.muted,
  }}>
    {children}
  </TableCell>
);

const ModulesSection = ({ state }: { state: SectionState<ChainModulesData> }) => {
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const {
    modules, enabledCount, activeCount, activeCountPrev, activeCountDelta,
    upsellPotential, upsellPotentialLabel, downgradeRisk, note,
  } = state.data;

  return (
    <Box>
      <SectionHeader
        icon={<ExtensionIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.purple}, ${COLORS.brand})`}
        title="Módulos do produto"
        subtitle="O que a rede contratou, o que ela realmente usa e o que sobrou de oportunidade"
      />

      <Box sx={{ display: 'grid', gap: 1.75 }}>
        <Box sx={cardSx}>
          <StatGrid min={165}>
            <Stat label="Contratados" value={fmtNum(enabledCount)} sub={`de ${fmtNum(modules.length)} módulos`} />
            <Stat label="Em uso" value={fmtNum(activeCount)} color={activeCount ? COLORS.success : COLORS.danger}
              sub={activeCountDelta === 0
                ? `estável vs 90 dias atrás (${fmtNum(activeCountPrev)})`
                : activeCountDelta > 0
                  ? `▲ ${fmtNum(activeCountDelta)} vs 90 dias atrás (${fmtNum(activeCountPrev)})`
                  : `▼ ${fmtNum(Math.abs(activeCountDelta))} vs 90 dias atrás (${fmtNum(activeCountPrev)})`} />
            <Tooltip arrow title={UPSELL_TOOLTIP}>
              <span>
                <Stat label="Potencial de upsell" value={fmtBrl(upsellPotential)} color={COLORS.purple}
                  sub={upsellPotentialLabel} />
              </span>
            </Tooltip>
            <Stat label="Risco de downgrade" value={fmtNum(downgradeRisk.length)}
              color={downgradeRisk.length ? COLORS.danger : COLORS.success}
              sub={downgradeRisk.length ? 'módulos pagos e parados' : 'nenhum módulo parado'} />
          </StatGrid>

          {downgradeRisk.length > 0 && (
            <Box sx={{
              mt: 1.75, px: 1.75, py: 1.25, borderRadius: 3, display: 'flex', alignItems: 'flex-start', gap: 1,
              bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.28)',
            }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: COLORS.danger, mt: '1px' }} />
              <Typography sx={{ fontSize: 12.5, color: COLORS.ink, lineHeight: 1.6 }}>
                Contratado e sem uso: <b>{downgradeRisk.join(', ')}</b>. Desligar módulo antecede cancelar contrato —
                {' '}é o que aparece primeiro numa renovação para baixo.
              </Typography>
            </Box>
          )}
        </Box>

        <Card title="Matriz contratado × em uso" hint="Contratado é a flag do cadastro; em uso exige artefato vigente">
          {!modules.length ? (
            <EmptyHint text="Nenhum módulo avaliado para esta rede." />
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 560 }}>
                <TableHead>
                  <TableRow>
                    <Th align="left">Módulo</Th>
                    <Th align="center">Contratado</Th>
                    <Th align="center">Em uso</Th>
                    <Th>Preço declarado</Th>
                    <Th align="left">Leitura</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modules.map(m => {
                    const downgrade = m.enabled && !m.active;
                    const upsell = !m.enabled && !!m.declaredValue && m.declaredValue > 0;
                    return (
                      <TableRow key={m.key} hover sx={{ bgcolor: downgrade ? 'rgba(239,68,68,0.04)' : undefined }}>
                        <TableCell sx={{ borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75, fontSize: 12.5, fontWeight: 700, color: COLORS.ink }}>
                          {m.label}
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75 }}>
                          <OnOffChip on={m.enabled} onLabel="sim" offLabel="não" />
                        </TableCell>
                        <TableCell align="center" sx={{ borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75 }}>
                          <OnOffChip on={m.active} onLabel="sim" offLabel="não" />
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75, fontSize: 12.5, color: COLORS.ink }}>
                          {m.declaredValue === null ? '—' : fmtBrl(m.declaredValue)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(148,163,184,0.16)', py: 0.75, fontSize: 11.5 }}>
                          {downgrade ? (
                            <Box component="span" sx={{ color: COLORS.danger, fontWeight: 700 }}>risco de downgrade</Box>
                          ) : upsell ? (
                            <Tooltip arrow title={UPSELL_TOOLTIP}>
                              <Box component="span" sx={{ color: COLORS.purple, fontWeight: 700 }}>oportunidade de upsell</Box>
                            </Tooltip>
                          ) : m.enabled ? (
                            <Box component="span" sx={{ color: COLORS.success, fontWeight: 700 }}>em uso</Box>
                          ) : (
                            <Box component="span" sx={{ color: COLORS.faint }}>não contratado, sem preço declarado</Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          <BackendNote>{note}</BackendNote>
        </Card>
      </Box>
    </Box>
  );
};

export default ModulesSection;
